import classNames from 'classnames';
import type { HTMLAttributes, ReactNode } from 'react';

import { Icon } from '../Icon';
import type { IconName } from '../Icon';

import styles from './Avatar.module.css';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';
/** Bottom-right presence dot. */
export type AvatarIndicator = 'online' | 'offline';

export interface AvatarProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  size?: AvatarSize;
  /** Image URL — covers the avatar when present. */
  src?: string;
  /** Full name — drives the alt text and the initials fallback. */
  name?: string;
  /** Overrides the computed initials (e.g. an overflow `"+5"`). */
  initials?: string;
  /** Placeholder glyph shown when there is no image and no name/initials. Defaults to `user-01`. */
  placeholderIcon?: IconName;
  /** Presence dot in the bottom-right. Superseded by `verified`/`company`/`badge`. */
  indicator?: AvatarIndicator;
  /** Built-in blue verified seal in the bottom-right. Superseded by `company`/`badge`. */
  verified?: boolean;
  /** Company logo URL — rendered as a white-framed round badge. Superseded by `badge`. */
  company?: string;
  /** Fully custom bottom-right badge — wins over `company`/`verified`/`indicator`. */
  badge?: ReactNode;
}

const MAX_INITIALS = 2;

function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, MAX_INITIALS);
}

// Seal geometry exported straight from the Figma "Verified tick" node (14×14) so the
// scalloped edge matches the design exactly. Fills its parent; color is `--blue-500`.
export function VerifiedSeal() {
  return (
    <svg className={styles.sealSvg} viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        fill="currentColor"
        d="M10.8109 2.47944C10.9439 2.80101 11.1991 3.05662 11.5205 3.19007L12.6474 3.65685C12.969 3.79006 13.2245 4.04557 13.3577 4.36717C13.4909 4.68877 13.4909 5.05011 13.3577 5.37171L12.8913 6.49781C12.758 6.81955 12.7578 7.18126 12.8917 7.50285L13.3573 8.6286C13.4234 8.78789 13.4574 8.95862 13.4574 9.13105C13.4574 9.30349 13.4235 9.47423 13.3575 9.63354C13.2915 9.79284 13.1948 9.93759 13.0728 10.0595C12.9509 10.1814 12.8061 10.2781 12.6468 10.344L11.5207 10.8105C11.1991 10.9434 10.9435 11.1987 10.8101 11.52L10.3433 12.6469C10.2101 12.9685 9.95457 13.224 9.63298 13.3572C9.31138 13.4905 8.95003 13.4905 8.62843 13.3572L7.50234 12.8908C7.18073 12.7579 6.81951 12.7582 6.49811 12.8916L5.3712 13.3577C5.04978 13.4906 4.68875 13.4905 4.36741 13.3574C4.04608 13.2243 3.79071 12.969 3.65741 12.6478L3.19049 11.5206C3.05752 11.199 2.8023 10.9434 2.48093 10.8099L1.35402 10.3431C1.03256 10.21 0.77713 9.95464 0.64387 9.63323C0.51061 9.31181 0.510429 8.95064 0.643368 8.62909L1.10981 7.50299C1.24269 7.18139 1.24242 6.82017 1.10906 6.49876L0.643283 5.37105C0.577245 5.21177 0.543241 5.04103 0.543214 4.8686C0.543186 4.69617 0.577135 4.52542 0.643122 4.36612C0.709108 4.20681 0.805838 4.06207 0.927785 3.94016C1.04973 3.81826 1.1945 3.72157 1.35383 3.65564L2.47992 3.1892C2.80122 3.05635 3.05667 2.80144 3.19022 2.48044L3.657 1.35354C3.79021 1.03194 4.04572 0.776428 4.36732 0.643217C4.68892 0.510006 5.05026 0.510006 5.37186 0.643217L6.49796 1.10966C6.81957 1.24254 7.18078 1.24227 7.50219 1.10891L8.62957 0.64394C8.95112 0.510804 9.31239 0.510831 9.63393 0.644016C9.95546 0.7772 10.2109 1.03264 10.3442 1.35415L10.8111 2.48139L10.8109 2.47944Z"
      />
      <path
        fill="var(--base-white)"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.7416 5.16505C9.83513 5.01815 9.86646 4.84012 9.82872 4.67012C9.79098 4.50012 9.68725 4.35207 9.54035 4.25855C9.39346 4.16503 9.21543 4.13369 9.04542 4.17143C8.87542 4.20917 8.72738 4.3129 8.63385 4.4598L6.06398 8.49792L4.88798 7.02792C4.77926 6.89193 4.62097 6.8047 4.44793 6.78542C4.27489 6.76614 4.10128 6.81639 3.96529 6.92511C3.8293 7.03383 3.74207 7.19212 3.72279 7.36516C3.70351 7.5382 3.75376 7.71181 3.86248 7.8478L5.61248 10.0353C5.6777 10.1169 5.76146 10.1818 5.85681 10.2245C5.95216 10.2673 6.05633 10.2866 6.16065 10.2809C6.26498 10.2753 6.36646 10.2448 6.45663 10.1921C6.54681 10.1393 6.62308 10.0657 6.6791 9.97755L9.7416 5.16505Z"
      />
    </svg>
  );
}

/**
 * User avatar — image, initials, or a placeholder glyph across six sizes, with an
 * optional presence dot, verified seal, or custom corner badge. Sizing is driven by a
 * component-local CSS var so the badge/dot scale with the avatar.
 *
 * @interim Ported from the reference apps; replaceable by the UI Kit without API breakage.
 */
export function Avatar({
  size = 'md',
  src,
  name,
  initials,
  placeholderIcon = 'user-01',
  indicator,
  verified = false,
  company,
  badge,
  className,
  ...rest
}: AvatarProps) {
  const resolvedInitials = initials ?? (name ? getInitials(name) : undefined);
  const showInitials = !src && Boolean(resolvedInitials);

  let cornerBadge: ReactNode = null;
  if (badge != null) {
    cornerBadge = <span className={styles.corner}>{badge}</span>;
  } else if (company != null) {
    cornerBadge = <img className={styles.company} src={company} alt="" />;
  } else if (verified) {
    cornerBadge = (
      <span className={styles.seal}>
        <VerifiedSeal />
      </span>
    );
  } else if (indicator != null) {
    cornerBadge = <span className={classNames(styles.dot, styles[indicator])} />;
  }

  // When the avatar renders no <img alt>, expose the name (if any) as the a11y label.
  const labelled = showInitials && name ? { role: 'img', 'aria-label': name } : {};

  return (
    <span
      className={classNames(styles.avatar, styles[`size-${size}`], className)}
      {...labelled}
      {...rest}
    >
      {src ? (
        <img className={styles.image} src={src} alt={name ?? ''} />
      ) : showInitials ? (
        <span className={styles.initials} aria-hidden="true">
          {resolvedInitials}
        </span>
      ) : (
        <span className={styles.placeholder}>
          <Icon name={placeholderIcon} />
        </span>
      )}
      {cornerBadge}
    </span>
  );
}
